import {
  ArrowRightOutlined,
  RightOutlined,
  WarningFilled,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
} from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useIntercom } from 'react-use-intercom'

import { type ProductSourceFeed as ProductSourceFeedType } from '@DataManagement/types/product'
import EditIcon from '@Icons/edit-01.svg?react'
import { useMeQuery } from '@Store/api'
import {
  useCreateFeedTargetMutation,
  useGetFeedTargetForMeQuery,
  usePreviewFeedTargetMutation,
  useRunManualFeedSyncMutation,
  useSaveFeedFieldMappingMutation,
  useUpdateFeedTargetMutation,
  useVisitFeedTargetForMeMutation,
} from '@Store/api/product'
import { useDataManagementState } from '@Store/slices/dataManagementSlice'
import { showErrorMessage, showToastMessage } from '@Utils/toast/toast-message'
import { isValidURL } from '@Utils/url/url-utils'

import RecentImports from './file-import/RecentImports'
import FeedUrlSourceAuthentication from './product/feed/FeedAuthentication'
import FeedBetaPanel from './product/feed/FeedBetaPanel'
import FeedMapFields, {
  type FeedMapFieldsActions,
} from './product/feed/FeedMapFields'
import ProductSourceBodyWrapper from './product/ProductSourceBodyWrapper'

// !!! Feed TODO (remaining Phase 6): CS-only feed-URL / source-switch
// transitions, empty-state Products alignment, downloadable error/warning
// logs, ProductSourceFeed decomposition.

const SYNC_IN_PROGRESS_TOOLTIP =
  'Sync settings cannot be changed while a sync is in progress.'
const MANUAL_SYNC_RUNNING_TOOLTIP =
  'A sync is currently in progress. Please try again when it completes.'
const ENTER_URL_FIRST_TOOLTIP = 'Enter a valid feed URL to continue.'
const EXISTING_PRODUCTS_TOOLTIP =
  'Feed Integration can only be set up while your product list is empty. Contact us to switch.'

function FeedSetupWizard(): JSX.Element {
  const [form] = Form.useForm()

  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false)
  // Tracked here rather than read from `form.isFieldsTouched()`: half of what
  // this form submits is not a form field. The sync toggle and the credentials
  // modal both write through setFieldValue, which antd does not count as a
  // touch, so the save button stayed disabled after enabling sync. It also
  // never un-touches, so the button stayed enabled after a successful save.
  const [isDirty, setIsDirty] = useState(false)
  // The mapping step's commit controls live in the dialog's footer, outside
  // the scrolling body, so a long feed cannot bury or clip them.
  const [mappingActions, setMappingActions] =
    useState<FeedMapFieldsActions | null>(null)
  const [isSyncConsentModalOpen, setIsSyncConsentModalOpen] = useState(false)
  const [showAllMappedFields, setShowAllMappedFields] = useState(false)
  const visitForRef = useRef<null | 'create' | 'edit'>(null)

  const { isProductManagementAlreadySet, selectedProductSource } =
    useDataManagementState()

  // A configured feed opens collapsed to a one-line header; the chevron expands
  // the full form. First-time setup has no feed to collapse, so it stays open.
  const [isFormCollapsed, setIsFormCollapsed] = useState(
    isProductManagementAlreadySet,
  )

  const url = Form.useWatch('path', form)
  const syncPeriodValue = Form.useWatch('syncPeriod', form)

  // Sync-enabled and overwrite-consent are UI-only intent (the API models
  // scheduled sync purely via `syncPeriod`), so they live in local state rather
  // than the form. Seed both from the persisted target: a feed that already
  // has a schedule has, by definition, already consented.
  const persistedSyncPeriod = (selectedProductSource as ProductSourceFeedType)
    ?.syncPeriod
  const initialSyncOn =
    persistedSyncPeriod != null && persistedSyncPeriod !== 'NONE'
  // Preselected frequency when the toggle turns on: keep a persisted schedule,
  // otherwise default to Monthly so the required select is never empty.
  const defaultSyncPeriod = initialSyncOn ? persistedSyncPeriod : 'MONTHLY'
  const [isSyncEnabled, setIsSyncEnabled] = useState(initialSyncOn)
  const [hasOverwriteConsent, setHasOverwriteConsent] = useState(initialSyncOn)

  const { show: showIntercom } = useIntercom()
  const { data: meData } = useMeQuery()

  // PRD §6.1: a feed's first sync computes the whole catalog from the feed, so
  // products already in the panel would be deleted by it — or trip the
  // deletion breaker and fail the run. Switching to Feed Integration is
  // therefore only offered from an empty product list; clearing it is a CS
  // action, so the user is pointed at support rather than left to guess. The
  // API refuses the create either way; this is so they find out before filling
  // in a feed URL rather than after.
  const isBlockedByExistingProducts =
    !isProductManagementAlreadySet && !!(meData as any)?.has_products

  const storedFeedSource = selectedProductSource as ProductSourceFeedType
  const [liveFeedSourceIsRunning, setLiveFeedSourceIsRunning] = useState(false)

  // `me` carries the saved target, but not the signals that only exist once a
  // run has finished — the columns the feed grew, the products the plan limit
  // turned away, and whether a sync is on right now. Those live on
  // /conveyor/target/me, which nothing was calling, so the banners below had
  // no data to render and never appeared. Polling while a sync is running is
  // what makes them show up when the run ends rather than on the next full
  // page load.
  const { data: liveFeedSource } = useGetFeedTargetForMeQuery(undefined, {
    skip: !isProductManagementAlreadySet,
    refetchOnMountOrArgChange: true,
    pollingInterval: liveFeedSourceIsRunning ? 10_000 : 0,
  })

  const feedSource = {
    ...storedFeedSource,
    ...(liveFeedSource ?? {}),
  } as ProductSourceFeedType
  const isSyncInProgress = !!feedSource?.isSyncInProgress

  // Poll only while a run is actually in flight, and stop as soon as it ends —
  // the last poll is the one that brings back the run's banners.
  useEffect(() => {
    setLiveFeedSourceIsRunning(isSyncInProgress)
  }, [isSyncInProgress])
  // Manual one-time sync is only offered when scheduled sync is OFF.
  const isScheduledSyncOff =
    !feedSource?.syncPeriod || feedSource.syncPeriod === 'NONE'
  // Once a feed is set up, the URL is CS-controlled — end users cannot edit
  // it (changing it can overwrite the whole product list).
  const isUrlLocked = isProductManagementAlreadySet
  const isSyncDisabled = isProductManagementAlreadySet && isScheduledSyncOff

  const [
    createFeedTarget,
    {
      isUninitialized: isCreateFeedTargetUninitialized,
      isSuccess: isCreateFeedTargetSuccess,
      isError: isCreateFeedTargetError,
      error: createFeedTargetError,
      isLoading: isCreateFeedTargetLoading,
      reset: resetCreateFeedTarget,
    },
  ] = useCreateFeedTargetMutation()

  const [
    updateFeedTarget,
    {
      isUninitialized: isUpdateFeedTargetUninitialized,
      isSuccess: isUpdateFeedTargetSuccess,
      isError: isUpdateFeedTargetError,
      error: updateFeedTargetError,
      isLoading: isUpdateFeedTargetLoading,
      reset: resetUpdateFeedTarget,
    },
  ] = useUpdateFeedTargetMutation()

  const [visitFeedTargetForMe, visitingFeedTargetForMeState] =
    useVisitFeedTargetForMeMutation()

  // First-time setup reads the feed without saving it, so an abandoned wizard
  // leaves no half-configured target behind. Editing an existing feed still
  // goes through visitFeedTargetForMe, which reads the stored row.
  const [previewFeedTarget, previewFeedTargetState] =
    usePreviewFeedTargetMutation()

  // Form values are held until the mapping is confirmed — the target and its
  // mapping are created together in a single request at that point.
  const pendingTargetBodyRef = useRef<Record<string, unknown> | null>(null)

  const [runManualFeedSync, { isLoading: isManualSyncLoading }] =
    useRunManualFeedSyncMutation()

  const onRunManualSync = async () => {
    try {
      await runManualFeedSync().unwrap()
      showToastMessage({
        message: 'Sync started. This may take a few minutes.',
      })
    } catch (error: any) {
      // 409 => a sync is already running; 400 => cooldown / scheduled-on.
      showErrorMessage({
        message:
          error?.data?.message ??
          'Could not start the sync. Please try again later.',
      })
    }
  }

  // PRD: enabling sync means the feed overwrites product data on every run, so
  // it needs explicit consent. We gate the ON transition behind a confirmation
  // modal instead of a separate checkbox: turning sync on opens the modal and
  // only commits once the user confirms. Consent already given (e.g. editing a
  // feed that is already syncing) skips the prompt; turning sync off is free.
  const onSyncToggleChange = (checked: boolean) => {
    if (!checked) {
      setIsSyncEnabled(false)
      setIsDirty(true)
      return
    }

    if (hasOverwriteConsent) {
      setIsSyncEnabled(true)
      setIsDirty(true)
      return
    }

    setIsSyncConsentModalOpen(true)
  }

  const onConfirmSyncConsent = () => {
    setHasOverwriteConsent(true)
    setIsSyncEnabled(true)
    setIsDirty(true)
    setIsSyncConsentModalOpen(false)
  }

  const onCancelSyncConsent = () => {
    // Consent withheld — leave sync off.
    setIsSyncConsentModalOpen(false)
  }

  const [
    saveFeedFieldMapping,
    {
      isUninitialized: isSaveFeedFieldMappingUninitialized,
      isLoading: isSaveFeedFieldMappingLoading,
      isSuccess: isSaveFeedFieldMappingSuccess,
      isError: isSaveFeedFieldMappingError,
      error: saveFeedFieldMappingError,
    },
  ] = useSaveFeedFieldMappingMutation()

  // A failed read only describes the URL + credentials it ran against, so
  // editing either clears the error rather than leaving it under a field the
  // user has since changed.
  const resetPreviewIfDone = () => {
    if (!previewFeedTargetState.isUninitialized) previewFeedTargetState.reset()
  }

  const onCredentialsSubmit = (
    type: 'basic' | 'api_key' | null,
    credentials:
      | { username: string; password: string }
      | { api_key: string }
      | null,
  ) => {
    form.setFieldValue('authType', type?.toUpperCase() ?? null)
    form.setFieldValue('authPayload', credentials)
    setIsDirty(true)

    resetPreviewIfDone()
    setIsCredentialsModalOpen(false)
  }

  const onSubmit = (values: any) => {
    visitingFeedTargetForMeState.reset()
    visitForRef.current = null

    // The API expresses scheduled sync solely through `syncPeriod` (NONE =
    // disabled), and the period field is unmounted when the toggle is off — so
    // translate the local toggle state into `syncPeriod` here.
    //
    // Credentials are set by the modal via setFieldValue and have no input of
    // their own, so they are read from the form store rather than `values`,
    // which only carries fields bound to a rendered control.
    const body = {
      ...values,
      authType: form.getFieldValue('authType'),
      authPayload: form.getFieldValue('authPayload'),
      syncPeriod: isSyncEnabled ? values.syncPeriod : 'NONE',
    }

    if (isProductManagementAlreadySet) {
      updateFeedTarget({
        id: (selectedProductSource as ProductSourceFeedType).id,
        body,
      })
      return
    }

    // Nothing is written yet: read the feed, show the mapping step, and create
    // the target together with the mapping once it is confirmed.
    pendingTargetBodyRef.current = body
    visitForRef.current = 'create'
    previewFeedTargetState.reset()
    previewFeedTarget({
      url: body.path,
      authType: body.authType,
      authPayload: body.authPayload,
    })
  }

  // Closes the mapping dialog by clearing whichever read opened it — the
  // dialog's open state is derived from that, so there is no second flag to
  // drift out of step with it.
  const closeMapping = () => {
    if (!previewFeedTargetState.isUninitialized) previewFeedTargetState.reset()
    if (!visitingFeedTargetForMeState.isUninitialized)
      visitingFeedTargetForMeState.reset()
    visitForRef.current = null
  }

  const onUpdateMappingClick = () => {
    visitForRef.current = 'edit'
    visitFeedTargetForMe()
  }

  const handleSaveMapping = (
    mapping: { fieldId: number; name: string }[],
  ): void => {
    const existingId = (selectedProductSource as ProductSourceFeedType)?.id

    if (existingId) {
      saveFeedFieldMapping({ id: existingId, mapping })
      return
    }

    // First-time setup: the target does not exist yet. Create it and its
    // mapping in one request so a rejected mapping leaves nothing behind.
    createFeedTarget({
      ...(pendingTargetBodyRef.current ?? {}),
      // Detected while reading the feed — the only point at which the real
      // format is known, since a server can mislabel the content type.
      contentType: previewFeedTargetState.data?.format,
      fields: mapping,
    } as any)
  }

  useEffect(() => {
    if (isCreateFeedTargetSuccess) {
      // The mapping was submitted with the target, so setup is complete here —
      // this used to reopen the mapping step, which is now what produced it.
      showToastMessage({ message: 'Feed integration created successfully.' })
      setIsDirty(false)
      pendingTargetBodyRef.current = null
      visitForRef.current = null
      if (!previewFeedTargetState.isUninitialized)
        previewFeedTargetState.reset()
    }
  }, [isCreateFeedTargetSuccess])

  useEffect(() => {
    if (isUpdateFeedTargetSuccess) {
      showToastMessage({ message: 'Feed target updated successfully.' })
      setIsDirty(false)
    }
  }, [isUpdateFeedTargetSuccess])

  useEffect(() => {
    if (isCreateFeedTargetError) {
      // Mapping validation now comes back through create, so the server's
      // message names the missing required property — far more useful than a
      // generic failure.
      showErrorMessage({
        message:
          (createFeedTargetError as any)?.data?.message ??
          'Failed to create feed integration.',
      })
    }
  }, [isCreateFeedTargetError, createFeedTargetError])

  useEffect(() => {
    if (previewFeedTargetState.isError) {
      showErrorMessage({
        message:
          (previewFeedTargetState.error as any)?.data?.message ??
          'Could not read this feed. Check the URL and any credentials it requires.',
      })
    }
  }, [previewFeedTargetState.isError])

  useEffect(() => {
    if (isUpdateFeedTargetError) {
      showErrorMessage({
        message: 'Failed to update feed target.',
      })
    }
  }, [isUpdateFeedTargetError, updateFeedTargetError])

  useEffect(() => {
    if (isSaveFeedFieldMappingError) {
      showErrorMessage({
        message: 'Failed to save feed field mapping.',
      })
      // TODO: send event to mixpanel/intercom about this
      return
    }
  }, [isSaveFeedFieldMappingError, saveFeedFieldMappingError])

  useEffect(() => {
    if (isSaveFeedFieldMappingSuccess) {
      showToastMessage({ message: 'Feed field mapping saved successfully.' })
      setIsDirty(false)

      if (!visitingFeedTargetForMeState.isUninitialized)
        visitingFeedTargetForMeState.reset()
      if (!isCreateFeedTargetUninitialized) resetCreateFeedTarget()
      if (!isUpdateFeedTargetUninitialized) resetUpdateFeedTarget()
    }
  }, [isSaveFeedFieldMappingSuccess])

  const previouslyMappedFields =
    (selectedProductSource as ProductSourceFeedType)?.conveyorTargetFields ?? []

  // The property-mapping control needs a persisted target to act on. That is
  // true once the feed is fully set up (`isProductManagementAlreadySet`, from
  // the `me` payload) — but also immediately after a first-time setup succeeds,
  // before the `me` refetch has flipped that flag. Surface the mapping step in
  // both cases so the user isn't left without a way to configure mappings.
  const isFeedSetupDone =
    isProductManagementAlreadySet || isCreateFeedTargetSuccess

  // First-time setup builds the mapping screen from the unsaved preview;
  // editing an existing feed reads the stored target. Same shape either way,
  // so FeedMapFields does not need to know which flow it is in.
  const hasSavedTarget = !!(selectedProductSource as ProductSourceFeedType)?.id
  const mapFieldsSourceState = hasSavedTarget
    ? visitingFeedTargetForMeState
    : previewFeedTargetState

  // In the create flow "saving the mapping" is the create call, since the
  // target and its mapping are written together.
  const mapFieldsSaveState = hasSavedTarget
    ? {
        isUninitialized: isSaveFeedFieldMappingUninitialized,
        isLoading: isSaveFeedFieldMappingLoading,
        isError: isSaveFeedFieldMappingError,
        isSuccess: isSaveFeedFieldMappingSuccess,
        error: saveFeedFieldMappingError ?? null,
      }
    : {
        isUninitialized: isCreateFeedTargetUninitialized,
        isLoading: isCreateFeedTargetLoading,
        isError: isCreateFeedTargetError,
        isSuccess: isCreateFeedTargetSuccess,
        error: createFeedTargetError ?? null,
      }

  const isFeedCollapsed = isProductManagementAlreadySet && isFormCollapsed

  return (
    <>
      <ProductSourceBodyWrapper
        title="Setup Feed URL"
        collapsible={isProductManagementAlreadySet}
        isCollapsed={isFeedCollapsed}
        onToggleCollapsed={() => setIsFormCollapsed((prev) => !prev)}
        headerInline={
          isFeedCollapsed ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-gray-400 text-sm truncate">
                {(selectedProductSource as ProductSourceFeedType)?.path}
              </span>
              {isSyncDisabled && (
                <Tooltip title="Sync is off, so your products no longer update automatically. Turn it on to resume.">
                  <WarningFilled className="!text-warning-600 text-base shrink-0" />
                </Tooltip>
              )}
            </span>
          ) : null
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          onValuesChange={() => setIsDirty(true)}
          autoComplete="off"
          initialValues={{
            ...(selectedProductSource
              ? {
                  authType: (selectedProductSource as ProductSourceFeedType)
                    .authType,
                  authPayload: (selectedProductSource as ProductSourceFeedType)
                    .authPayload,
                  path: (selectedProductSource as ProductSourceFeedType).path,
                }
              : {}),
          }}
        >
          {isBlockedByExistingProducts && (
            <Alert
              type="warning"
              showIcon
              className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
              description={
                <span className="text-sm text-warning-700">
                  Feed Integration replaces your entire product list on every
                  sync, so it can only be set up while your product list is
                  empty. To switch,{' '}
                  <a
                    className="text-primary-600 font-medium"
                    onClick={(e) => {
                      e.preventDefault()
                      showIntercom()
                    }}
                  >
                    contact us
                  </a>{' '}
                  and we will clear your current products for you.
                </span>
              }
            />
          )}
          {/* The active source (and the contact-us route to change it) is
              already stated by the source picker above, so no banner repeats it
              here. The sync-off warning moved onto the sync row itself. */}
          {(feedSource?.newUnmappedFields?.length ?? 0) > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-6 border border-solid border-blue-200 rounded-lg bg-blue-25"
              description={
                <span className="text-sm text-gray-700">
                  New unmapped fields detected in your feed. You can map them
                  via &lsquo;Edit Mapping&rsquo;.
                </span>
              }
            />
          )}
          {feedSource?.mineUrlDomainMismatch ? (
            <Alert
              type="warning"
              showIcon
              className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
              description={
                <span className="text-sm text-warning-700">
                  The product URLs in your feed are on{' '}
                  <strong>
                    {feedSource.mineUrlDomainMismatch.domains.join(', ')}
                  </strong>
                  {feedSource.mineUrlDomainMismatch.storeDomain ? (
                    <>
                      , but your store domain is set to{' '}
                      <strong>
                        {feedSource.mineUrlDomainMismatch.storeDomain}
                      </strong>
                    </>
                  ) : (
                    ', but no store domain is set for your account'
                  )}
                  . {feedSource.mineUrlDomainMismatch.count}{' '}
                  {feedSource.mineUrlDomainMismatch.count === 1
                    ? 'product is'
                    : 'products are'}{' '}
                  being tracked as competitor URLs instead of your own, so their
                  prices will not sync from your feed.{' '}
                  <a
                    className="text-primary-600 font-medium"
                    onClick={(e) => {
                      e.preventDefault()
                      showIntercom()
                    }}
                  >
                    Contact us
                  </a>{' '}
                  to correct your store domain.
                </span>
              }
            />
          ) : null}
          {(feedSource?.productLimitSkipped ?? 0) > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
              description={
                <span className="text-sm text-warning-700">
                  Product limit reached. {feedSource.productLimitSkipped}{' '}
                  {feedSource.productLimitSkipped === 1
                    ? 'product'
                    : 'products'}{' '}
                  could not be added during the last sync.
                </span>
              }
            />
          )}
          <Row gutter={16} className="mb-8">
            <Col xs={24} sm={24} md={12} lg={9}>
              <div className="flex flex-col">
                <p className="text-gray-700 font-medium text-base">
                  Enter link to your file
                </p>
                <p className="text-gray-500 text-sm">
                  XML, CSV, JSON and XLSX formats are supported
                </p>
              </div>
            </Col>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 12 }}
              lg={{ span: 15 }}
            >
              {/* No separate validate step: continuing reads the feed, which
                  proves it is reachable, authenticated and parseable in one
                  request. */}
              <Form.Item
                label="File URL"
                className="[&>div>.ant-form-item-label]:!pb-0 [&>div>.ant-form-item-label]:text-gray-700 [&>div>.ant-form-item-label]:font-medium [&>div>.ant-form-item-label]:mb-0 [&>div>.ant-form-item-label]:text-sm mb-0 flex-1"
                htmlFor="path"
                name="path"
                {...(previewFeedTargetState.isError && {
                  validateStatus: 'error',
                })}
                {...(previewFeedTargetState.isLoading && {
                  validateStatus: 'validating',
                })}
              >
                <Input
                  allowClear={!isUrlLocked}
                  disabled={isUrlLocked}
                  placeholder="Enter feed URL"
                  /* Two shapes to style, not one: with allowClear antd wraps
                     the input in an affix span and className lands there, so
                     the padding has to move to the inner input; once the feed
                     is locked there is no clear button, no wrapper, and
                     className lands on the input itself. Scoping the reset to
                     the wrapper keeps the locked field from losing its
                     padding and sitting flush against the border. */
                  className="flex h-[40px] justify-between border rounded-lg w-full [&.ant-input-affix-wrapper]:!p-0 [&.ant-input-affix-wrapper]:!pr-2 [&>input]:!p-2.5 [&:not(.ant-input-affix-wrapper)]:!p-2.5 [&>input]:!leading-none [&>input]:text-[100%] !leading-none text-[100%] text-gray-900 bg-white-100 !border-gray-300 [&.ant-input-status-error]:!border-error-300"
                  autoComplete="off"
                  onChange={resetPreviewIfDone}
                  onClear={resetPreviewIfDone}
                />
              </Form.Item>
              <div className="pt-1 pb-3">
                <Button
                  variant="link"
                  color="default"
                  className="px-0 text-primary-600 hover:!bg-transparent "
                  onClick={() => {
                    setIsCredentialsModalOpen(true)
                  }}
                >
                  Credentials <EditIcon />
                </Button>
                {/* authType/authPayload deliberately have no Form.Item: a
                    `name` with no child makes antd warn, and authPayload is an
                    object, so binding it to a hidden input would only trade
                    that for a React one. The credentials modal writes both via
                    setFieldValue and onSubmit reads them back off the form. */}
              </div>
            </Col>
          </Row>
          <Row gutter={16} className="mb-8">
            <Col xs={24} sm={24} md={12} lg={6} offset={0}>
              <p className="text-gray-700 font-medium text-base">
                Configure sync schedule
              </p>
            </Col>
            <Col
              xs={{ span: 24, offset: 0 }}
              sm={{ span: 24, offset: 0 }}
              md={{ span: 12, offset: 0 }}
              lg={{ span: 15, offset: 3 }}
            >
              <Space.Compact block className="items-start justify-stretch">
                <Tooltip
                  title={isSyncInProgress ? SYNC_IN_PROGRESS_TOOLTIP : ''}
                >
                  {/* Controlled directly (not via Form.Item's injected
                      onChange) so the ON transition can open the consent modal
                      and only commit on confirm. syncEnabled still lives in the
                      form via setFieldValue + initialValues. */}
                  <span className="inline-flex relative top-[2px]">
                    <Switch
                      checked={!!isSyncEnabled}
                      disabled={isSyncInProgress}
                      onChange={onSyncToggleChange}
                      className="bg-gray-300 h-6 [&.ant-switch-checked]:bg-primary-600 [&.ant-switch-checked:hover]:!bg-primary-400 [&>.ant-switch-handle]:h-5 [&>.ant-switch-handle]:w-5 [&.ant-switch-checked>.ant-switch-handle]:ml-[-2px]"
                    />
                  </span>
                </Tooltip>
                <div className="ml-3">
                  <p className="text-gray-700 font-medium text-base flex items-center gap-2">
                    Automatically sync my products
                    {isSyncDisabled && (
                      // The old full-width "sync is disabled" banner reduced to
                      // a warning icon on the line it describes; the message
                      // itself shows on hover.
                      <Tooltip title="Sync is off, so your products no longer update automatically. Turn it on to resume.">
                        <WarningFilled className="!text-warning-600 text-base" />
                      </Tooltip>
                    )}
                  </p>
                  <p className="text-gray-500 text-base mb-4">
                    Choose how often you want your products in Prisync to be
                    updated based on your feed
                  </p>
                  {isSyncEnabled && (
                    <Form.Item
                      label=""
                      htmlFor="syncPeriod"
                      name="syncPeriod"
                      preserve={false}
                      initialValue={defaultSyncPeriod}
                      className="min-w-52 w-[50%]"
                    >
                      <Select
                        size="large"
                        disabled={isSyncInProgress}
                        className="[&>.ant-select-selector]:border [&>.ant-select-selector]:!border-gray-300 [&>.ant-select-selector]:bg-gray-50 hover:border-gray-300 hover:bg-gray-100 text-gray-700 !rounded-l-lg focus:!border-gray-300 [&.ant-select-focused]:!border-gray-300 [&.ant-select-focused]:!shadow-gray-300"
                        placeholder="Select sync period"
                        options={[
                          {
                            value: 'DAILY',
                            label: 'Every 24 hours',
                            className:
                              '!text-base [&.ant-select-item-option-selected]:!bg-primary-300 [&.ant-select-item-option-selected]:!text-primary-700',
                          },
                          {
                            value: 'WEEKLY',
                            label: 'Weekly',
                            className:
                              '!text-base [&.ant-select-item-option-selected]:!bg-primary-300 [&.ant-select-item-option-selected]:!text-primary-700',
                          },
                          {
                            value: 'MONTHLY',
                            label: 'Monthly',
                            className:
                              '!text-base [&.ant-select-item-option-selected]:!bg-primary-300 [&.ant-select-item-option-selected]:!text-primary-700 ',
                          },
                        ]}
                      />
                    </Form.Item>
                  )}
                </div>
              </Space.Compact>
            </Col>
          </Row>
          {isFeedSetupDone ? (
            <Row gutter={16} className="mb-8">
              <Col xs={24} sm={24} md={12} lg={6} offset={0}>
                <p className="text-gray-700 font-medium text-base">
                  Configure property mapping
                </p>
              </Col>
              <Col
                xs={{ span: 24, offset: 0 }}
                sm={{ span: 24, offset: 0 }}
                md={{ span: 12, offset: 0 }}
                lg={{ span: 15, offset: 3 }}
              >
                <Space wrap className="gap-2">
                  {(showAllMappedFields
                    ? previouslyMappedFields
                    : previouslyMappedFields.slice(0, 4)
                  ).map((f) => (
                    <Tag
                      key={f?.id ?? f?.name}
                      className="inline-flex items-center gap-1 m-0 rounded-[6px] px-2.5 py-1 text-xs leading-[18px] bg-white border-solid border-[#D0D5DD]"
                    >
                      <span className="text-[#667085]">{f?.name}</span>
                      <ArrowRightOutlined
                        className="text-[#98A2B3]"
                        width={12}
                        height={12}
                        aria-hidden
                      />
                      <span className="font-medium text-[#344054]">
                        {f?.conveyorField?.label}
                      </span>
                    </Tag>
                  ))}
                  {previouslyMappedFields.length > 4 && (
                    <Button
                      type="link"
                      className="!px-0 !py-0 !text-sm !leading-none !text-primary-600 hover:!bg-transparent gap-0"
                      onClick={() =>
                        setShowAllMappedFields(!showAllMappedFields)
                      }
                    >
                      {showAllMappedFields
                        ? 'Show Less'
                        : `Show All Mappings (${previouslyMappedFields.length})`}
                      {!showAllMappedFields ? (
                        <RightOutlined className="!text-primary-600 text-sm" />
                      ) : (
                        <></>
                      )}
                    </Button>
                  )}
                </Space>
                <div className="pt-1 pb-3">
                  <Button
                    variant="link"
                    color="default"
                    disabled={!hasSavedTarget || isUpdateFeedTargetLoading}
                    loading={visitingFeedTargetForMeState.isLoading}
                    className="px-0 text-primary-600 hover:!bg-transparent "
                    onClick={onUpdateMappingClick}
                  >
                    {previouslyMappedFields?.length > 0 ? 'Edit' : 'Create'}
                    {' Mapping'}{' '}
                    <EditIcon
                      className={
                        hasSavedTarget ? '' : '[&>path]:stroke-gray-400'
                      }
                    />
                  </Button>
                </div>
              </Col>
            </Row>
          ) : (
            <></>
          )}
          <Row gutter={16}>
            <Col span={24} className="flex justify-end">
              <Space>
                <Tooltip
                  title={
                    isBlockedByExistingProducts
                      ? EXISTING_PRODUCTS_TOOLTIP
                      : isValidURL(url)
                        ? ''
                        : ENTER_URL_FIRST_TOOLTIP
                  }
                >
                  {/* Span keeps the tooltip working while the button is
                      disabled — disabled controls swallow hover. */}
                  <span className="inline-flex">
                    <Button
                      type={
                        isProductManagementAlreadySet ? 'default' : 'primary'
                      }
                      size="large"
                      htmlType="submit"
                      /* min-width rather than a fixed w-44: the loading spinner is
                          inserted alongside the label, and at a fixed width
                          "Continue to Mapping" plus a spinner overflowed the
                          button, clipping the spinner against its rounded
                          edge. */
                      className={`!px-5 !py-2.5 !leading-none min-w-44 whitespace-nowrap border ${isProductManagementAlreadySet ? 'bg-white hover:!bg-gray-100 !border-gray-300 !text-gray-700 disabled:!bg-white-100 disabled:!border-gray-300 disabled:!text-gray-400 disabled:!cursor-not-allowed' : 'bg-primary-600 hover:!bg-primary-700 !border-primary-600 !text-white disabled:!bg-primary-200 disabled:!border-primary-300 disabled:!text-gray-400 disabled:!cursor-not-allowed'}`}
                      disabled={
                        // The feed is read on continue, so a well-formed URL is
                        // all this can check up front.
                        !isValidURL(url) ||
                        isCreateFeedTargetLoading ||
                        isUpdateFeedTargetLoading ||
                        previewFeedTargetState.isLoading ||
                        visitingFeedTargetForMeState.isLoading ||
                        isSyncInProgress ||
                        isBlockedByExistingProducts ||
                        // Only block on missing mappings when editing an
                        // existing setup; in the create flow the mapping is
                        // collected in the next step, before anything is saved.
                        (isProductManagementAlreadySet &&
                          previouslyMappedFields?.length === 0) ||
                        // Sync on but no period chosen would submit an invalid
                        // scheduled-sync configuration.
                        (isSyncEnabled && !syncPeriodValue) ||
                        !isDirty
                      }
                      loading={
                        isUpdateFeedTargetLoading ||
                        isCreateFeedTargetLoading ||
                        previewFeedTargetState.isLoading
                      }
                    >
                      {/* Create no longer saves anything here — it reads the
                          feed and opens the mapping step, and the target is
                          written once that is confirmed. */}
                      {isProductManagementAlreadySet
                        ? 'Update Setup'
                        : 'Continue to Mapping'}
                    </Button>
                  </span>
                </Tooltip>
                {isProductManagementAlreadySet && isScheduledSyncOff && (
                  <Tooltip
                    title={isSyncInProgress ? MANUAL_SYNC_RUNNING_TOOLTIP : ''}
                  >
                    <Button
                      type="primary"
                      size="large"
                      htmlType="button"
                      onClick={onRunManualSync}
                      loading={isManualSyncLoading}
                      disabled={isSyncInProgress || isManualSyncLoading}
                      className="!px-5 !py-2.5 !leading-none min-w-44 whitespace-nowrap border bg-primary-600 hover:!bg-primary-700 !border-primary-600 !text-white disabled:!bg-primary-200 disabled:!border-primary-300 disabled:!text-gray-400 disabled:!cursor-not-allowed"
                    >
                      Run One-Time Sync
                    </Button>
                  </Tooltip>
                )}
              </Space>
            </Col>
          </Row>
        </Form>
        <FeedUrlSourceAuthentication
          isOpen={isCredentialsModalOpen}
          setIsOpen={setIsCredentialsModalOpen}
          onSubmit={onCredentialsSubmit}
          initialValues={{
            authType:
              ((selectedProductSource as ProductSourceFeedType)?.authType as
                | 'BASIC'
                | 'API_KEY'
                | null) ?? null,
            authPayload:
              ((selectedProductSource as ProductSourceFeedType)?.authPayload as
                | { username: string; password: string }
                | { api_key: string }
                | null) ?? null,
          }}
        />
        <Modal
          open={isSyncConsentModalOpen}
          onCancel={onCancelSyncConsent}
          centered
          title="Enable automatic sync"
          okText="Enable automatic sync"
          cancelText="Cancel"
          onOk={onConfirmSyncConsent}
          okButtonProps={{
            className:
              '!bg-primary-600 hover:!bg-primary-700 !border-primary-600 !text-white',
          }}
        >
          <p className="text-gray-700 text-sm leading-relaxed">
            While automatic sync is on, every sync <b>replaces</b> your Prisync
            product data with the data in this feed. Products missing from the
            feed can be removed, and manual edits can be overwritten. This can't
            be undone.{' '}
            <a
              href="https://help.prisync.com"
              target="_blank"
              rel="noreferrer"
              className="text-primary-600"
            >
              Learn More
            </a>
          </p>
        </Modal>
      </ProductSourceBodyWrapper>
      {/* A dialog rather than a section appended to the page. Mapping is a
          step, not a panel: inline it landed below the fold, so the user had
          to find it, and the setup form above stayed live underneath — editing
          the URL mid-mapping would have mapped one feed and saved another. */}
      <Modal
        open={mapFieldsSourceState.isUninitialized === false}
        onCancel={closeMapping}
        afterClose={() => setMappingActions(null)}
        footer={
          mappingActions ? (
            // Message on the left, actions on the right: the footer is fixed,
            // so a warning appearing here cannot shift the table the user is
            // working in.
            <div className="flex items-center justify-between gap-4">
              <span className="text-left text-sm text-error-600">
                {mappingActions.blockedReason ?? ''}
              </span>
              <Space>
                <Button
                  type="text"
                  onClick={mappingActions.cancel}
                  className="!text-gray-500 hover:!bg-transparent"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={mappingActions.saving}
                  disabled={!mappingActions.canSave}
                  onClick={mappingActions.save}
                  className="bg-primary-600 hover:!bg-primary-700 !border-primary-600 !text-white disabled:!bg-primary-200 disabled:!border-primary-300 disabled:!text-gray-400"
                >
                  {mappingActions.saveLabel}
                </Button>
              </Space>
            </div>
          ) : null
        }
        centered
        destroyOnHidden
        width={960}
        title="Map your feed columns"
        // The body is the only thing that scrolls. 70vh alone measured the
        // viewport and ignored the dialog's own header and padding, so a long
        // feed pushed the dialog past the screen and the page behind it
        // scrolled too — two scrollbars for one list. overflow-x is clipped
        // rather than scrolled: a long column value should be truncated, not
        // widen the dialog.
        styles={{
          body: {
            maxHeight: 'min(70vh, calc(100dvh - 200px))',
            overflowY: 'auto',
            overflowX: 'hidden',
            // The scrollbar would otherwise be laid over the table's right
            // edge, clipping the border and crowding the property dropdowns
            // against it. Reserving the gutter also stops the content
            // reflowing by a scrollbar's width when the list gets long enough
            // to need one.
            scrollbarGutter: 'stable',
          },
        }}
      >
        <FeedMapFields
          visitResultState={{
            ...mapFieldsSourceState,
            isLoading: mapFieldsSourceState.isLoading,
            isError: mapFieldsSourceState.isError,
            isSuccess: mapFieldsSourceState.isSuccess,
            error: mapFieldsSourceState.error ?? null,
            data: mapFieldsSourceState.data ?? null,
          }}
          saveResultState={mapFieldsSaveState}
          onSubmit={handleSaveMapping}
          onCancel={closeMapping}
          onActionsChange={setMappingActions}
          visitFor={visitForRef.current ?? null}
        />
      </Modal>

      <RecentImports type={['conveyor']} />
    </>
  )
}

/**
 * Feed Integration is in beta, and during the beta only support configures it.
 *
 * The wizard below is not retired — it is the tool support uses, from a
 * login-as session inside the customer's account, because it is the only place
 * that can read a feed, suggest a mapping and store credentials. Retailers get
 * {@link FeedBetaPanel} instead: their feed, read-only, and Intercom.
 *
 * The backend enforces the same split (ConveyorBetaSupportOnlyGuard refuses
 * every conveyor write that is not a login-as session), so this switch is what
 * the customer sees rather than what stops them.
 */
function ProductSourceFeed(): JSX.Element {
  const { data: meData, isLoading } = useMeQuery()

  // Rendering the beta panel first and swapping it for the wizard a moment
  // later would flash the customer's read-only view at support on every visit.
  if (isLoading) return <></>

  return (meData as any)?.logged_in_as ? <FeedSetupWizard /> : <FeedBetaPanel />
}

export default ProductSourceFeed
